/**
 * Project list — the home screen after the 7g navigation refactor.
 *
 * Loads the user's active projects (the backend filters out archived
 * rows by default, so there's no "archived" section here per §7.8 Q3).
 * Create flow opens the modal; post-create we navigate into the new
 * project so the user can immediately add documents or start a
 * conversation.
 *
 * Empty state nudges toward either creating a project or continuing
 * in the standalone /coaches flow — users who just want a quick chat
 * don't need to create a project first.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header.jsx";
import ProjectCard from "../components/ProjectCard.jsx";
import PipelineBoard from "../components/PipelineBoard.jsx";
import CreateProjectModal from "../components/CreateProjectModal.jsx";
import EmptyState from "../components/EmptyState.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import SetupIncompleteBanner from "../components/SetupIncompleteBanner.jsx";
import { useStore } from "../store/useStore.js";
import { COLLAB_MODE_ENABLED } from "../config.js";

export default function ProjectList() {
  const navigate = useNavigate();
  const projects = useStore((s) => s.projects);
  const loading = useStore((s) => s.projectsLoading);
  const error = useStore((s) => s.projectsError);
  const loadProjects = useStore((s) => s.loadProjects);

  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [view, setView] = useState(
    () => localStorage.getItem("projectListView") || "pipeline",
  );

  const handleViewChange = (v) => {
    setView(v);
    localStorage.setItem("projectListView", v);
  };

  useEffect(() => {
    // v4.1 Phase 36.8: when collab mode is on, fetch both owned + shared
    // projects so the "Shared with me" section populates without a second
    // round-trip. Backend returns `role` per project.
    loadProjects({ includeShared: COLLAB_MODE_ENABLED });
  }, [loadProjects]);

  const query = search.trim().toLowerCase();
  const visibleProjects = projects
    .filter((p) => showArchived || p.status !== "archived")
    .filter((p) => !query || p.name.toLowerCase().includes(query));
  const ownedProjects = COLLAB_MODE_ENABLED
    ? visibleProjects.filter((p) => !p.role || p.role === "owner")
    : visibleProjects;
  const sharedProjects = COLLAB_MODE_ENABLED
    ? visibleProjects.filter((p) => p.role && p.role !== "owner")
    : [];
  // Keep the old name for the pipeline view / legacy call sites.
  const filteredProjects = ownedProjects;
  const hasArchived = projects.some((p) => p.status === "archived");

  const handleCreated = (project) => {
    setModalOpen(false);
    navigate(`/projects/${project.id}`);
  };

  return (
    <>
      <Header />
      <main className="home">
        <div className="container">
          <SetupIncompleteBanner />
          <div className="home-intro">
            <h1>
              Your <span className="accent">projects</span>
            </h1>
            <p>
              Create a project to group documents and conversations around a
              prospect, customer, or initiative. Or jump into a{" "}
              <Link to="/coaches" className="inline-link">
                standalone coaching chat
              </Link>{" "}
              without a project.
            </p>
          </div>

          {error && <ErrorBubble message={error} />}

          <div className="project-list-header">
            <input
              type="search"
              className="project-search"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search projects"
            />
            <div className="view-toggle" role="group" aria-label="View mode">
              <button
                type="button"
                className={`view-toggle__btn${view === "grid" ? " view-toggle__btn--active" : ""}`}
                onClick={() => handleViewChange("grid")}
                aria-pressed={view === "grid"}
              >
                Grid
              </button>
              <button
                type="button"
                className={`view-toggle__btn${view === "pipeline" ? " view-toggle__btn--active" : ""}`}
                onClick={() => handleViewChange("pipeline")}
                aria-pressed={view === "pipeline"}
              >
                Pipeline
              </button>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setModalOpen(true)}
            >
              + New project
            </button>
          </div>

          {hasArchived && (
            <label className="show-archived-toggle">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          )}

          {loading && projects.length === 0 ? (
            <div className="project-grid">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="project-card project-card-skeleton">
                  <LoadingSkeleton variant="line" size="sm" />
                  <LoadingSkeleton variant="line" />
                  <LoadingSkeleton variant="line" size="short" />
                </div>
              ))}
            </div>
          ) : projects.length === 0 ? (
            <EmptyState
              title="No projects yet"
              description="Create your first project to start uploading documents and grounding conversations in your own research."
              action={
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setModalOpen(true)}
                >
                  Create a project
                </button>
              }
            />
          ) : filteredProjects.length === 0 ? (
            <EmptyState
              title="No matches"
              description={`No projects match "${search}".`}
            />
          ) : view === "pipeline" ? (
            <PipelineBoard projects={filteredProjects} />
          ) : (
            <>
              <div className="project-grid">
                {filteredProjects.map((p) => (
                  <ProjectCard key={p.id} project={p} />
                ))}
              </div>
              {COLLAB_MODE_ENABLED && sharedProjects.length > 0 && (
                <section
                  className="project-list-shared"
                  aria-label="Projects shared with me"
                >
                  <h2 className="project-list-shared-title">Shared with me</h2>
                  <div className="project-grid">
                    {sharedProjects.map((p) => (
                      <ProjectCard key={p.id} project={p} showRole />
                    ))}
                  </div>
                </section>
              )}
              {/* Mobile accordion — view toggle is hidden so always provide pipeline */}
              <div className="grid-mobile-fallback">
                <PipelineBoard projects={filteredProjects} />
              </div>
            </>
          )}
        </div>
      </main>

      <CreateProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}
